/*
 * Purpose: Manage password creation state, validation, and generation logic.
 * Boundary: Custom hook only; owns all state, validation, and actions; no UI rendering.
 * Invariants:
 * - Valid passwords must match, have length >= 15, and include upper, lower, digit, special.
 * - Uses auth service if provided; otherwise generates locally.
 * - Clipboard operations use browser API.
 * - ASCII only; comments and strings are plain ASCII.
 */

// Imports
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useToast } from '../../toast/toast-slim';

// Constants
const MIN_PASSWORD_LENGTH = 10;
const DEFAULT_GENERATE_LENGTH = 15;
const DEFAULT_WORD_LIST = [
    'apple', 'banana', 'cherry', 'date', 'elder', 'fig', 'grape', 'honey', 'ice', 'jam',
    'kiwi', 'lemon', 'mango', 'nut', 'orange', 'pear', 'quince', 'rasp', 'straw', 'tanger',
    'uva', 'vanilla', 'water', 'xylophone', 'yellow', 'zest',
];
const CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()';

// Hook useCreatePassword:
// Manages password inputs, validation, and generator integration
export function useCreatePassword({
    auth,
    onChange = () => { },
    hideSecureMessage,
    showChips,
    showHeading
} = {}) {
    // Get toastr from context
    const toastr = useToast();

    // States: Inputs, validation, UI toggles, and generator
    const [inputState, setInputState] = useState({
        password: '',
        confirmPassword: '',
    });
    const [validationState, setValidationState] = useState({
        isValid: false,
        status: '',
    });
    const [uiState, setUiState] = useState({
        showPassword: true,
        showSelectors: false,
        showSecureMessage: true,
    });
    const [genState, setGenState] = useState({
        genType: 'chars',
        length: DEFAULT_GENERATE_LENGTH,
    });

    // Validator: Enforce rules (length, chars, match)
    const validatePassword = useCallback((pwd, confirm) => {
        if (!pwd || pwd !== confirm) return false;
        return (
            pwd.length >= MIN_PASSWORD_LENGTH &&
            /[A-Z]/.test(pwd) &&
            /[a-z]/.test(pwd) &&
            /\d/.test(pwd) &&
            /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd)
        );
    }, []);

    // Derived: Password validity
    const isValid = useMemo(
        () => validatePassword(inputState.password, inputState.confirmPassword),
        [inputState.password, inputState.confirmPassword, validatePassword]
    );

    // Effect: Update length on generator type change
    useEffect(() => {
        setGenState((prev) => ({
            ...prev,
            length: prev.genType === 'chars' ? DEFAULT_GENERATE_LENGTH : 4,
        }));
    }, [genState.genType]);

    // Generator: Create password via auth or local with toast feedback
    const generatePassword = useCallback(async () => {
        setValidationState((prev) => ({ ...prev, status: '' }));
        try {
            let generated;
            if (auth) {
                var reqDto = { type: genState.genType, length: genState.length };

                var results = await auth.generatePassword(reqDto);
                if (results.isSuccess && results.data) {
                    generated = results.data.password;
                } else {
                    toastr.error('Failed to generate password');
                    throw new Error('No password in auth results');
                }
            } else if (genState.genType === 'chars') {
                generated = Array.from({ length: genState.length }, () =>
                    CHARS[Math.floor(Math.random() * CHARS.length)]
                ).join('');
            } else {
                // Diceware-style phrase
                const phraseWords = Array.from({ length: genState.length }, () =>
                    DEFAULT_WORD_LIST[Math.floor(Math.random() * DEFAULT_WORD_LIST.length)]
                );
                generated = phraseWords.join('-') + Math.floor(Math.random() * 10) + '!';
                generated = generated.charAt(0).toUpperCase() + generated.slice(1);
            }
            setInputState({ password: generated, confirmPassword: generated });
            toastr.success('Password generated successfully');
        } catch (err) {
            toastr.error('Failed to generate password');
            setValidationState((prev) => ({
                ...prev,
                status: 'Generation failed. Check server response.',
            }));
        }
    }, [auth, genState.genType, genState.length, toastr]);

    // Handlers: Input updates
    const onPasswordChange = useCallback((e) => {
        setInputState((prev) => ({ ...prev, password: e.target.value }));
        setValidationState((prev) => ({ ...prev, status: '' }));
    }, []);

    const onConfirmChange = useCallback((e) => {
        setInputState((prev) => ({ ...prev, confirmPassword: e.target.value }));
        setValidationState((prev) => ({ ...prev, status: '' }));
    }, []);

    // Handlers: UI toggles
    const toggleShow = useCallback(
        () => setUiState((prev) => ({ ...prev, showPassword: !prev.showPassword })),
        []
    );
    const toggleSecureMessage = useCallback(
        () => setUiState((prev) => ({ ...prev, showSecureMessage: !prev.showSecureMessage })),
        []
    );
    const setShowSelectors = useCallback(
        (show) => setUiState((prev) => ({ ...prev, showSelectors: show })),
        []
    );

    // Handler: Clipboard copy
    const onCopy = useCallback(
        () => navigator.clipboard.writeText(inputState.password),
        [inputState.password]
    );

    // Handlers: Generator controls
    const setGenType = useCallback(
        (type) => setGenState((prev) => ({ ...prev, genType: type })),
        []
    );
    const setLength = useCallback(
        (len) => setGenState((prev) => ({ ...prev, length: len })),
        []
    );

    // Effect: Notify parent of changes
    useEffect(() => {
        onChange({ isValid, password: inputState.password });
    }, [isValid, inputState.password, onChange]);

    // Return: Grouped state and handlers
    return {
        password: inputState.password,
        confirmPassword: inputState.confirmPassword,
        showPassword: uiState.showPassword,
        isValid,
        genType: genState.genType,
        setGenType,
        length: genState.length,
        setLength,
        status: validationState.status,
        onPasswordChange,
        onConfirmChange,
        generatePassword,
        showSelectors: uiState.showSelectors,
        setShowSelectors,
        toggleShow,
        onCopy,
        showSecureMessage: uiState.showSecureMessage,
        toggleSecureMessage,
        hideSecureMessage,
        showChips,
        showHeading
    };
}
